import http from '../../http-common'
import type {
  AgendaEvent,
  AgendaFilters,
  AgendaKind,
  CreateTaskData,
  Task,
  TaskAction,
  TaskNote,
  TaskTipo,
  TaskStatus,
} from '@/types/agenda'

export type { AgendaEvent, AgendaFilters, AgendaKind, CreateTaskData, Task, TaskAction, TaskNote, TaskTipo, TaskStatus }
import { devLog } from '@/services/logger'
import { stripInvalidId } from '@/services/utils'

class AgendaDataService {
  // ===== AGENDA API =====

  /**
   * Lista eventos da agenda com filtros
   * @param filters Filtros para a agenda
   */
  getAgenda(filters?: AgendaFilters): Promise<any> {
    const params = new URLSearchParams()

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'me' && value === true) {
            params.append('me', 'true')
          } else if (key !== 'me') {
            params.append(key, value.toString())
          }
        }
      })
    }

    return http.get('/agenda/' + (params.toString() ? `?${params.toString()}` : ''))
  }

  // ===== TAREFAS API =====

  /**
   * Lista tarefas com filtros
   * @param filters Query string com filtros
   */
  getTarefas(filters?: string): Promise<any> {
    const queryString = filters ? filters : ''
    return http.get('/tarefa/' + queryString)
  }

  /**
   * Busca uma tarefa específica
   * @param id ID da tarefa
   */
  getTarefa(id: string): Promise<any> {
    return http.get(`/tarefa/${id}/`)
  }

  /**
   * Cria uma nova tarefa
   * @param data Dados da tarefa
   */
  createTarefa(data: CreateTaskData): Promise<any> {
    devLog('🚀 AgendaService.createTarefa() - Dados recebidos (keys):', Object.keys(data || {}))

    // Validar dados obrigatórios
    if (!data.titulo || !data.tipo || !data.quando || !data.responsavel) {
      throw new Error('Dados obrigatórios ausentes: titulo, tipo, quando, responsavel')
    }

    // Garantir que lead seja number ou undefined (não string)
    const cleanData = { ...data }
    if (cleanData.lead && typeof cleanData.lead === 'string') {
      cleanData.lead = parseInt(cleanData.lead, 10)
    }

    devLog('📤 AgendaService.createTarefa() - Dados limpos para envio (keys):', Object.keys(cleanData))

    return http.post('/tarefa/', cleanData)
  }

  /**
   * Atualiza uma tarefa existente
   * @param id ID da tarefa
   * @param data Dados para atualizar
   */
  updateTarefa(id: string, data: any): Promise<any> {
    const payload = stripInvalidId(data)
    return http.patch(`/tarefa/${id}/`, payload)
  }

  // ===== AÇÕES DE TAREFAS =====

  /**
   * Reagenda uma tarefa
   * @param taskId ID da tarefa
   * @param data Dados do reagendamento (quando, reason)
   */
  rescheduleTask(taskId: string, data: TaskAction): Promise<any> {
    return http.post(`/tarefa/${taskId}/reschedule/`, data)
  }

  /**
   * Reabre uma tarefa
   * @param taskId ID da tarefa
   * @param data Dados da reabertura (quando, reason)
   */
  reopenTask(taskId: string, data: TaskAction): Promise<any> {
    return http.post(`/tarefa/${taskId}/reopen/`, data)
  }

  /**
   * Completa uma tarefa
   * @param taskId ID da tarefa
   * @param data Dados da conclusão (done_at opcional)
   */
  completeTask(taskId: string, data: TaskAction = {}): Promise<any> {
    return http.post(`/tarefa/${taskId}/complete/`, data)
  }

  /**
   * Cancela uma tarefa
   * @param taskId ID da tarefa
   * @param data Dados do cancelamento (canceled_at, reason)
   */
  cancelTask(taskId: string, data: TaskAction): Promise<any> {
    return http.post(`/tarefa/${taskId}/cancel/`, data)
  }

  /**
   * Marca tarefa como no-show
   * @param taskId ID da tarefa
   * @param data Dados do no-show (event_at, reason)
   */
  noShowTask(taskId: string, data: TaskAction): Promise<any> {
    return http.post(`/tarefa/${taskId}/no_show/`, data)
  }

  /**
   * Adiciona uma nota à tarefa
   * @param taskId ID da tarefa
   * @param data Dados da nota (texto)
   */
  addNoteToTask(taskId: string, data: TaskAction): Promise<any> {
    return http.post(`/tarefa/${taskId}/notes/`, data)
  }

  // ===== DADOS AUXILIARES =====

  /**
   * Lista responsáveis/usuários da empresa
   */
  getResponsaveis(): Promise<any> {
    return http.get('/companyuser/' + localStorage.getItem('empSlug') + '/usersFromMyCompany')
  }

  /**
   * Lista leads para filtros
   */
  getLeads(): Promise<any> {
    return http.get('/lead/')
  }
}

export default new AgendaDataService()
